import { useState } from 'react';
import type { Invoice } from '@faktorplus/shared';
import { useInvoiceList } from '../../api/invoices';
import { InvoiceView } from '../invoices/invoice-view';
import { AssignInvoiceForm } from '../invoices/assign-invoice-form';
import { Button } from '../../components/ui/button';
import { EmptyState, ErrorBox, LoadingBlock } from '../../components/ui/feedback';
import { formatDate, formatMoney } from '../../lib/format';
import pageStyles from '../../components/layout/page.module.css';
import styles from './assign-page.module.css';

/**
 * The accountant's queue: assign each new invoice to a project, a manager and
 * a category, with the scan visible beside the form.
 *
 * "Zapisz i następna" keeps the rhythm — seven invoices are seven identical
 * movements, not seven round trips through a list.
 */
export function AssignQueuePage() {
  const { data, isLoading, error } = useInvoiceList({
    status: 'NEW',
    sortBy: 'receivedDate',
    sortDir: 'asc',
    pageSize: 50,
  });

  const [index, setIndex] = useState(0);

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorBox>Nie udało się pobrać faktur.</ErrorBox>;

  const invoices = data?.items ?? [];
  const current = invoices[Math.min(index, invoices.length - 1)];

  if (!current) {
    return (
      <>
        <header className={pageStyles.header}>
          <h1 className={pageStyles.title}>Przypisz fakturę</h1>
        </header>
        <EmptyState
          title="Wszystko przypisane"
          description="Żadna faktura nie czeka na przypisanie do budowy."
        />
      </>
    );
  }

  return (
    <>
      <header className={pageStyles.header}>
        <h1 className={pageStyles.title}>Przypisz fakturę</h1>
        <span className={pageStyles.subtitle}>
          {Math.min(index + 1, invoices.length)} z {invoices.length} · pozostałe czekają w kolejce
        </span>
      </header>

      <div className={styles.split}>
        <div className={styles.scanSide}>
          <InvoiceView invoice={current} />
        </div>

        <div className={styles.formSide}>
          <InvoiceIdentity invoice={current} />

          <AssignInvoiceForm
            key={current.id}
            invoice={current}
            submitLabel="Zapisz i następna →"
            onSaved={() => setIndex((value) => value + 1)}
            secondaryAction={
              <Button
                variant="ghost"
                onClick={() => setIndex((value) => (value + 1) % invoices.length)}
              >
                Pomiń
              </Button>
            }
          />
        </div>
      </div>
    </>
  );
}

function InvoiceIdentity({ invoice }: { invoice: Invoice }) {
  return (
    <div className={styles.identity}>
      <p className={styles.vendor}>{invoice.vendorName}</p>
      <p className={styles.docNumber}>{invoice.number}</p>

      <div className={styles.amounts}>
        <div>
          <p className={styles.amountLabel}>Netto</p>
          <p className={styles.amountValue}>{formatMoney(invoice.netAmount)}</p>
        </div>
        <div>
          <p className={styles.amountLabel}>Brutto</p>
          <p className={styles.amountValue}>{formatMoney(invoice.grossAmount)}</p>
        </div>
        <div>
          <p className={styles.amountLabel}>Wpływ</p>
          <p className={styles.amountValue}>{formatDate(invoice.receivedDate)}</p>
        </div>
      </div>
    </div>
  );
}
